import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/common/ErrorBoundary';
// Lazy load components
const AdminLayout = React.lazy(() => import('./components/AdminLayout'));
const CompanyLayout = React.lazy(() => import('./components/CompanyLayout'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const AdminProjectManagement = React.lazy(() => import('./pages/AdminProjectManagement'));
const AdminVerificationManagement = React.lazy(() => import('./pages/AdminVerificationManagement'));
const AdminVerificationDetail = React.lazy(() => import('./pages/AdminVerificationDetail'));
const AdminCompanyManagement = React.lazy(() => import('./pages/AdminCompanyManagement'));
const AdminUserManagement = React.lazy(() => import('./pages/AdminUserManagement'));
const SurveyManagement = React.lazy(() => import('./pages/SurveyManagement'));
const CompanyStatusManagement = React.lazy(() => import('./pages/CompanyStatusManagement'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));

// 보호된 라우트 컴포넌트
const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredRole?: 'admin' | 'company' }> = ({ 
  children, 
  requiredRole 
}) => {
  const { state } = useAuth();

  if (state.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (!state.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && state.user?.role !== requiredRole) {
    // 권한이 없는 경우 역할에 따라 리다이렉트
    if (state.user?.role === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    } else {
      return <Navigate to="/company/company-status" replace />;
    }
  }

  return <>{children}</>;
};

// 관리자 전용 라우트
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <ProtectedRoute requiredRole="admin">{children}</ProtectedRoute>;
};

// 기업담당자 전용 라우트
const CompanyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <ProtectedRoute requiredRole="company">{children}</ProtectedRoute>;
};

// 인증된 사용자 라우트 (역할 구분 없음) - 현재 미사용
// const AuthenticatedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
//   return <ProtectedRoute>{children}</ProtectedRoute>;
// };

// 메인 앱 컴포넌트
const AppRoutes: React.FC = () => {
  const { state } = useAuth();

  // 로그인하지 않은 경우 로그인 페이지로
  if (!state.isAuthenticated && !state.isLoading) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // 로그인한 경우 역할에 따라 리다이렉트
  if (state.isAuthenticated && state.user) {
    const { role } = state.user;
    
    return (
      <Routes>
        {/* 로그인 페이지 - 이미 로그인한 경우 역할에 따라 리다이렉트 */}
        <Route 
          path="/login" 
          element={
            role === 'admin' 
              ? <Navigate to="/admin/dashboard" replace />
              : <Navigate to="/company/company-status" replace />
          } 
        />

        {/* 관리자 라우트 */}
        <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="projects" element={<AdminProjectManagement />} />
          <Route path="verification" element={<AdminVerificationManagement />} />
          <Route path="verification/:surveyId" element={<AdminVerificationDetail />} />
          <Route path="companies" element={<AdminCompanyManagement />} />
          <Route path="users" element={<AdminUserManagement />} />
          <Route path="reports" element={<div>통계 및 리포트 페이지</div>} />
          <Route path="emails" element={<div>이메일 관리 페이지</div>} />
          <Route path="export" element={<div>데이터 내보내기 페이지</div>} />
          <Route path="settings" element={<div>시스템 설정 페이지</div>} />
        </Route>

        {/* 기업담당자 라우트 */}
        <Route path="/company" element={<CompanyRoute><CompanyLayout /></CompanyRoute>}>
          <Route index element={<Navigate to="/company/company-status" replace />} />
          <Route path="company-status" element={<CompanyStatusManagement />} />
          <Route path="surveys" element={<SurveyManagement />} />
        </Route>

        {/* 기본 리다이렉트 */}
        <Route path="*" element={<Navigate to={role === 'admin' ? '/admin/dashboard' : '/company/company-status'} replace />} />
      </Routes>
    );
  }

  return null;
};

// Loading component
const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
      <p className="mt-4 text-muted-foreground">로딩 중...</p>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <AppRoutes />
          </Suspense>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
